// Passkeys in the browser, and the PRF output that unlocks the archive.
//
// Two ceremonies that look like one button (§13). WebAuthn proves which
// account this is; the PRF extension yields a secret that never leaves the
// authenticator, and that secret is what the account key is wrapped under.
//
// The PRF evaluation input is a fixed application constant, not a per-account
// salt. That is deliberate: with discoverable credentials the server does not
// know which credential will answer until the assertion is over, so a
// per-credential eval input would need a round trip the ceremony cannot make.
// The PRF output is already unique per credential, which is where the
// separation actually comes from.

import * as K from './keys.ts';
import type { Credential } from './keys.ts';

const PRF_INPUT_LABEL = 'wa-archive/v1/prf';

const b64uToBytes = (s: string) =>
  Uint8Array.from(atob(s.replace(/-/g, '+').replace(/_/g, '/')), (c) => c.charCodeAt(0));
const bytesToB64u = (b: Uint8Array) =>
  btoa(String.fromCharCode(...b)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

async function prfInput(): Promise<Uint8Array> {
  const bytes = new TextEncoder().encode(PRF_INPUT_LABEL);
  return new Uint8Array(await crypto.subtle.digest('SHA-256', bytes as BufferSource));
}

export const supported = () =>
  typeof PublicKeyCredential !== 'undefined' && !!navigator.credentials;

/** Whether this authenticator will give us a key, not just a login. */
export async function prfAvailable(): Promise<boolean> {
  try {
    return await PublicKeyCredential.isConditionalMediationAvailable?.() !== undefined;
  } catch {
    return false;
  }
}

interface Options {
  challenge: string;
  user?: { id: string; name: string; displayName: string };
  excludeCredentials?: Array<{ id: string; type: string; transports?: string[] }>;
  allowCredentials?: Array<{ id: string; type: string; transports?: string[] }>;
  [k: string]: unknown;
}

function decodeCreation(o: Options): PublicKeyCredentialCreationOptions {
  const out = { ...o } as unknown as PublicKeyCredentialCreationOptions & {
    user: { id: unknown }; excludeCredentials?: Array<{ id: unknown }>;
  };
  out.challenge = b64uToBytes(o.challenge) as unknown as BufferSource;
  if (!o.user) throw new Error('registration options are missing a user handle');
  out.user = { ...o.user, id: b64uToBytes(o.user.id) } as unknown as PublicKeyCredentialUserEntity;
  if (o.excludeCredentials)
    out.excludeCredentials = o.excludeCredentials.map((c) => ({ ...c, id: b64uToBytes(c.id) })) as never;
  return out as PublicKeyCredentialCreationOptions;
}

function decodeRequest(o: Options): PublicKeyCredentialRequestOptions {
  const out = { ...o } as unknown as PublicKeyCredentialRequestOptions & {
    allowCredentials?: Array<{ id: unknown }>;
  };
  out.challenge = b64uToBytes(o.challenge) as unknown as BufferSource;
  if (o.allowCredentials?.length)
    out.allowCredentials = o.allowCredentials.map((c) => ({ ...c, id: b64uToBytes(c.id) })) as never;
  return out as PublicKeyCredentialRequestOptions;
}

/** The shape the server's verifier expects. */
function encodeAttestation(c: PublicKeyCredential) {
  const r = c.response as AuthenticatorAttestationResponse;
  return {
    id: c.id,
    rawId: bytesToB64u(new Uint8Array(c.rawId)),
    type: c.type,
    clientExtensionResults: {},
    response: {
      clientDataJSON: bytesToB64u(new Uint8Array(r.clientDataJSON)),
      attestationObject: bytesToB64u(new Uint8Array(r.attestationObject)),
      transports: r.getTransports?.() ?? [],
    },
  };
}

function encodeAssertion(c: PublicKeyCredential) {
  const r = c.response as AuthenticatorAssertionResponse;
  return {
    id: c.id,
    rawId: bytesToB64u(new Uint8Array(c.rawId)),
    type: c.type,
    clientExtensionResults: {},
    response: {
      clientDataJSON: bytesToB64u(new Uint8Array(r.clientDataJSON)),
      authenticatorData: bytesToB64u(new Uint8Array(r.authenticatorData)),
      signature: bytesToB64u(new Uint8Array(r.signature)),
      ...(r.userHandle ? { userHandle: bytesToB64u(new Uint8Array(r.userHandle)) } : {}),
    },
  };
}

function prfResult(c: PublicKeyCredential): Uint8Array | null {
  const ext = c.getClientExtensionResults() as {
    prf?: { results?: { first?: ArrayBuffer | Uint8Array } };
  };
  const first = ext.prf?.results?.first;
  return first ? new Uint8Array(first instanceof Uint8Array ? first : new Uint8Array(first)) : null;
}

type Post = (path: string, body: unknown) => Promise<Record<string, unknown>>;

export interface SignInResult {
  accessToken: string;
  refreshToken: string;
  account: { accountId: string; credentials?: Credential[] } & Record<string, unknown>;
  accountKey: Uint8Array;
  recoveryWords?: string[];
}

/**
 * First device on a new account: register a passkey, then assert with it to
 * obtain the PRF secret, then create the account key and wrap it under both
 * the passkey and a recovery code.
 *
 * The extra assertion is not avoidable in practice — most authenticators
 * evaluate PRF only during `get`, not during `create`.
 */
export async function registerPasskey(post: Post): Promise<SignInResult> {
  const opts = (await post('/v1/auth/register/options', {})) as {
    challengeId: string; options: Options;
  };
  const created = (await navigator.credentials.create({
    publicKey: decodeCreation(opts.options),
  })) as PublicKeyCredential | null;
  if (!created) throw new Error('Passkey registration was cancelled.');

  const verified = (await post('/v1/auth/register/verify', {
    challengeId: opts.challengeId,
    response: encodeAttestation(created),
    platform: 'web',
  })) as { accessToken: string; refreshToken: string; prfSalt: string; account: SignInResult['account'] };

  // Now assert, to get the secret the authenticator will only produce on `get`.
  const { prfOutput, assertion } = await assertWithPrf(post);
  if (!prfOutput)
    throw new Error(
      'This authenticator cannot produce a key (no PRF support). Use a platform passkey — '
      + 'Touch ID, Windows Hello or an Android device.',
    );

  const salt = b64uToBytes(verified.prfSalt.replace(/\+/g, '-').replace(/\//g, '_'));
  const passkeyKek = await K.kek(prfOutput, salt, 'passkey');

  const accountKey = K.newAccountKey();
  const wrappedByPasskey = await K.wrapAccountKey(accountKey, passkeyKek);

  const { words } = K.generateRecoveryCode();
  const recoverySalt = crypto.getRandomValues(new Uint8Array(32));
  const recoveryKek = await K.kek(await K.deriveRecoveryEntropy(words), recoverySalt, 'recovery');
  const wrappedByRecovery = await K.wrapAccountKey(accountKey, recoveryKek);

  const saved = (await post('/v1/auth/login/verify', {
    ...assertion,
    credentials: [
      { method: 'passkey', version: 1,
        credentialSalt: K.toB64(salt), wrappedKey: K.toB64(wrappedByPasskey) },
      { method: 'recovery', version: 1,
        credentialSalt: K.toB64(recoverySalt), wrappedKey: K.toB64(wrappedByRecovery) },
    ],
  })) as unknown as SignInResult;

  return { ...saved, accountKey, recoveryWords: words };
}

async function assertWithPrf(post: Post) {
  const opts = (await post('/v1/auth/login/options', {})) as {
    challengeId: string; options: Options;
  };
  const publicKey = decodeRequest(opts.options);
  const got = (await navigator.credentials.get({
    publicKey: {
      ...publicKey,
      extensions: {
        ...(publicKey.extensions ?? {}),
        prf: { eval: { first: await prfInput() } },
      } as AuthenticationExtensionsClientInputs,
    },
  })) as PublicKeyCredential | null;
  if (!got) throw new Error('Sign-in was cancelled.');
  return {
    prfOutput: prfResult(got),
    assertion: { challengeId: opts.challengeId, response: encodeAssertion(got) },
  };
}

/** Returning device: assert, then unwrap the account key with the PRF secret. */
export async function loginWithPasskey(post: Post): Promise<SignInResult> {
  const { prfOutput, assertion } = await assertWithPrf(post);
  const res = (await post('/v1/auth/login/verify', assertion)) as unknown as SignInResult;

  if (!prfOutput) throw new Error('This authenticator did not return a key (no PRF support).');

  // Several passkeys can be registered, each wrapping the account key under
  // its own PRF secret. The one that matters is the one whose salt matches the
  // device that just answered.
  const salt = (res as unknown as { prfSalt?: string }).prfSalt;
  const passkeys = res.account.credentials?.filter((c) => c.method === 'passkey') ?? [];
  const passkey = salt
    ? passkeys.find((c) => c.credentialSalt === salt)
    : passkeys[0];
  if (!passkey)
    throw new Error(
      passkeys.length
        ? 'This passkey has no key material on this archive. Add it from a device that already works, or use your recovery code.'
        : 'This archive has no passkey key material. Use your recovery code.',
    );

  const kek = await K.kek(prfOutput, K.fromB64(passkey.credentialSalt), 'passkey');
  let accountKey: Uint8Array;
  try {
    accountKey = await K.unwrapAccountKey(K.fromB64(passkey.wrappedKey), kek);
  } catch {
    throw new Error('That passkey does not unlock this archive.');
  }
  return { ...res, accountKey };
}

/**
 * Register a passkey for an archive whose key you already hold (D-011).
 *
 * The difference from addThisDevice is where the account key comes from: there
 * it is recovered from twenty-four words, here it is already in memory because
 * the service handed it over at sign-in. Everything after that is identical —
 * a new passkey means a new PRF secret, so the same key is wrapped again under
 * it.
 *
 * This does not stop the service holding its own copy. Taking that away is a
 * separate, deliberate act, and it is refused until this has succeeded.
 */
/**
 * Give an existing archive a passkey — and, when it has none, a recovery code.
 *
 * The recovery half is not optional dressing. Taking the held key off us with
 * only a passkey leaves exactly one way into twelve years of someone's life,
 * on one device. The words are the second way, and they are generated here
 * because this is the only moment the account key is in hand.
 */
export async function addPasskeyToAccount(
  post: Post, accountKey: Uint8Array, want: { withRecovery?: boolean } = {},
): Promise<{ recoveryWords?: string[] }> {
  const opts = (await post('/v1/auth/register/options', {})) as {
    challengeId: string; options: Options;
  };
  const created = (await navigator.credentials.create({
    publicKey: decodeCreation(opts.options),
  })) as PublicKeyCredential | null;
  if (!created) throw new Error('Passkey registration was cancelled.');

  const verified = (await post('/v1/auth/register/verify', {
    challengeId: opts.challengeId,
    response: encodeAttestation(created),
    platform: 'web',
  })) as { prfSalt: string };

  const { prfOutput, assertion } = await assertWithPrf(post);
  if (!prfOutput) throw new Error('This authenticator cannot produce a key (no PRF support).');

  const kek = await K.kek(prfOutput, K.fromB64(verified.prfSalt), 'passkey');
  const wrapped = await K.wrapAccountKey(accountKey, kek);

  const credentials: Array<Record<string, unknown>> = [{
    method: 'passkey', version: 1,
    credentialSalt: verified.prfSalt,
    wrappedKey: K.toB64(wrapped),
  }];

  let words: string[] | undefined;
  if (want.withRecovery) {
    words = K.generateRecoveryCode().words;
    const recoverySalt = crypto.getRandomValues(new Uint8Array(32));
    const recoveryKek = await K.kek(
      await K.deriveRecoveryEntropy(words), recoverySalt, 'recovery');
    credentials.push({
      method: 'recovery', version: 1,
      credentialSalt: K.toB64(recoverySalt),
      wrappedKey: K.toB64(await K.wrapAccountKey(accountKey, recoveryKek)),
    });
  }

  await post('/v1/auth/login/verify', { ...assertion, credentials });
  return { recoveryWords: words };
}

/**
 * Add this device to an archive that already exists.
 *
 * A new passkey produces a new PRF secret, which cannot unwrap a key wrapped
 * under a different one. So the account key has to be recovered first — from
 * the recovery code — and then wrapped again for this device. There is no way
 * around this that does not involve the server holding something that unwraps.
 */
export async function addThisDevice(
  post: Post, credentials: Credential[], recoveryWords: string[],
): Promise<SignInResult> {
  const accountKey = await unlockWithRecoveryCode(credentials, recoveryWords);

  const opts = (await post('/v1/auth/register/options', {})) as {
    challengeId: string; options: Options;
  };
  const created = (await navigator.credentials.create({
    publicKey: decodeCreation(opts.options),
  })) as PublicKeyCredential | null;
  if (!created) throw new Error('Passkey registration was cancelled.');

  const verified = (await post('/v1/auth/register/verify', {
    challengeId: opts.challengeId,
    response: encodeAttestation(created),
    platform: 'web',
  })) as { prfSalt: string };

  const { prfOutput, assertion } = await assertWithPrf(post);
  if (!prfOutput) throw new Error('This authenticator cannot produce a key (no PRF support).');

  const salt = K.fromB64(verified.prfSalt);
  const kek = await K.kek(prfOutput, salt, 'passkey');
  const wrapped = await K.wrapAccountKey(accountKey, kek);

  const saved = (await post('/v1/auth/login/verify', {
    ...assertion,
    credentials: [{ method: 'passkey', version: 1,
                    credentialSalt: K.toB64(salt), wrappedKey: K.toB64(wrapped) }],
  })) as unknown as SignInResult;

  return { ...saved, accountKey };
}

/** The only reset path. Twenty-four words, and nothing else will do. */
export async function unlockWithRecoveryCode(
  credentials: Credential[], words: string[],
): Promise<Uint8Array> {
  const cred = credentials.find((c) => c.method === 'recovery');
  if (!cred) throw new Error('This account has no recovery credential.');
  const kek = await K.kek(await K.deriveRecoveryEntropy(words), K.fromB64(cred.credentialSalt), 'recovery');
  try {
    return await K.unwrapAccountKey(K.fromB64(cred.wrappedKey), kek);
  } catch {
    throw new Error('That recovery code does not match this account.');
  }
}
